import React from 'react'
import type { Currency } from '@/shared/models/moneyModel'
import { isNil } from 'lodash'
import Paragraph from './Paragraph'
import Title from './Title'
import { useLocalization } from '@/shared/hooks/useLocalization'
import { EMPTY_VALUE } from '@hello-aria/ui/empty-value'

export interface AmountWithCurrencyProps {
    amount?: number | null
    currency?: Currency | null
    containerClassName?: string
    type?: 'cell' | 'paragraph' | 'header'
}

const AmountWithCurrency = ({ amount, currency, type = 'paragraph' }: AmountWithCurrencyProps) => {
    const { localizeAmounts } = useLocalization()
    if (isNil(amount) || isNaN(amount) || isNil(currency)) {
        switch (type) {
            case 'cell':
                return <span data-testid="__amount-with-currency-cell__">{EMPTY_VALUE}</span>
            case 'header':
                return <Title.TripleXl data-testid="__amount-with-currency-title__">{EMPTY_VALUE}</Title.TripleXl>
            case 'paragraph':
            default:
                return <Paragraph data-testid="__amount-with-currency-paragraph__">{EMPTY_VALUE}</Paragraph>
        }
    }

    switch (type) {
        case 'cell':
            return (
                <div className="flex justify-end gap-1" data-testid="__amount-with-currency-cell__">
                    <span>{localizeAmounts(amount, currency, {})}</span>
                    <span data-testid="__amount-with-currency-cell-currency__" className="text-textSecondary">
                        {currency}
                    </span>
                </div>
            )
        case 'header':
            return (
                <Title.TripleXl data-testid="__amount-with-currency-header__">
                    {localizeAmounts(amount, currency)}
                    <span
                        className="font-normal text-textSecondary ml-2"
                        data-testid="__amount-with-currency-header-currency__"
                    >
                        {currency}
                    </span>
                </Title.TripleXl>
            )
        case 'paragraph':
        default:
            return (
                <div className="flex gap-1" data-testid="__amount-with-currency-paragraph__">
                    <Paragraph>
                        {localizeAmounts(amount, currency)}
                        <span
                            className="text-textSecondary ml-2"
                            data-testid="__amount-with-currency-paragraph-currency__"
                        >
                            {currency}
                        </span>
                    </Paragraph>
                </div>
            )
    }
}

export default AmountWithCurrency
